/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, MouseEventHandler, Fragment } from "react"
import { Row, Col, Checkbox, Button, Tag } from "@duxton-web/ui"
import { AddImageUniversal } from "@duxton-web/icons"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import classnames from "classnames"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Item } from "types/models/menu"
import { Image } from "components/Image"
import useMenuContext from "hooks/useMenuContext"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import {
  isSpecialItem,
  isItemAvailable,
  AvailableStatusesText,
  availableStatusesUsage,
  availableStatusesStyle,
  useItemWithBackTimeMessageText,
  availableStatuses,
} from "utils/item_helper"
import { OverflowTip } from "components/common"
import { ItemAvailabilityDropdownDuxton } from "components/dropdowns"
import { SellingTimeTag } from "components/tags"
import { getSellingTimeByID } from "utils/selling_time_helper"
import { useSellingTimes } from "hooks/useSellingTimes"
import {
  getCategoryByID,
  getSubDepartmentByID,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import dayjs from "dayjs"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { useTranslationsMap } from "common/utils/i18n"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import styles from "./MenuItemRow.modules.css"

interface IProps {
  item: Item
  showSelection?: boolean
  showDropdown?: boolean
  className?: string
  onClickAddPhoto?: Function
  highlightString?: string
}

export const MenuItemRow: FC<IProps> = ({
  item,
  showSelection,
  showDropdown,
  className,
  onClickAddPhoto = () => {},
  highlightString,
}: IProps) => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const handleCheck = (value: boolean) => {
    dispatch(actionCreator.menu.multiSelectItem(item, value))
  }

  const { sellingTimes } = useSellingTimes()
  const {
    itemSelections,
    isSellingTimeStructure,
    categories,
    subDepartments,
    isMartDepartmentStructure,
    itemFilter,
    enableAlcoholSetting,
    overviewSubDepartments,
  } = useSelector(
    (state: AppState) => ({
      isSellingTimeStructure: state.menu.isSellingTimeStructure,
      itemSelections: state.menu.drawer.itemSelections,
      categories: state.menu.categories,
      subDepartments: state.menu.subDepartments,
      isMartDepartmentStructure: state.menu.isMartDepartmentStructure,
      itemFilter: state.menu.itemFilter,
      enableAlcoholSetting: state.menu.enableAlcoholSetting,
      overviewSubDepartments: state.menu.overview.subDepartments,
    }),
    shallowEqual,
  )

  const { isEditAllowed } = useMenuEditorStatus()

  const { isServiceBasedMenuEnabled } = useMenuContext()

  const category = getCategoryByID(item?.categoryID, categories)

  const subDepartment = getSubDepartmentByID(
    item?.subDepartmentID,
    enablePartialMenu ? overviewSubDepartments : subDepartments,
  )

  const parentSellingTimeID = isMartDepartmentStructure
    ? subDepartment?.sellingTimeID
    : category?.sellingTimeID

  const showSellingTimeTag =
    isSellingTimeStructure && item.sellingTimeID !== parentSellingTimeID

  const selected = itemSelections?.[item?.itemID ?? ""] ?? false

  const spanDropdown = showDropdown ? 8 : 0

  const isUnavailable = [
    !isItemAvailable(item),
    !enableAlcoholSetting && isSpecialItem(item),
  ].some(val => val)

  const availableUntilText = useItemWithBackTimeMessageText(
    dayjs(item.availableAt),
  )
  const availableStatusesText = AvailableStatusesText(
    availableStatusesUsage.MENU_TAG,
  )
  const renderUnavailableTag = () => {
    let tagText = ""
    if (
      item.availableStatus === availableStatuses.UNAVAILABLE_UNTIL_CUSTOM ||
      item.availableStatus === availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK
    ) {
      tagText = availableUntilText
    } else {
      tagText = availableStatusesText[item.availableStatus as number]
    }

    return (
      isUnavailable &&
      tagText && (
        <Col>
          <Tag
            size="xsmall"
            color={
              availableStatusesStyle[availableStatusesUsage.MENU_TAG][
                item.availableStatus as number
              ] ?? ""
            }
          >
            {tagText}
          </Tag>
        </Col>
      )
    )
  }

  const handleClickSelected: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation()
    if (!showSelection) {
      dispatch(
        actionCreator.menu.toggleDrawer({
          showItemSelection: true,
        }),
      )
    }
  }
  /*
    because backend api for selling time is not ready
    uncomment code below to test the changes, remove after backend ready
    don't forget to import { mockSellingSchedules } from "tests/mockMenuState"
  */
  // item.sellingTime = mockSellingSchedules[0]

  const textMap = useTranslationsMap({
    addPhotoButton: "itemPhotoCoverage.button.addPhoto",
  })

  // Function to highlight search text in item name
  const highlightText = (text: string, search?: string) => {
    if (!search || !search.trim()) {
      return text
    }

    const searchLower = search.toLowerCase()
    const textLower = text.toLowerCase()
    const index = textLower.indexOf(searchLower)

    if (index === -1) {
      return text
    }

    const before = text.substring(0, index)
    const match = text.substring(index, index + search.length)
    const after = text.substring(index + search.length)

    return (
      <Fragment>
        {before}
        <span className={styles.highlightText}>{match}</span>
        {after}
      </Fragment>
    )
  }

  return (
    <Row
      wrap={false}
      gutter={16}
      className={classnames(styles.rowStyle, className)}
    >
      <Col>
        <Image
          src={item.imageURL}
          width={73}
          height={73}
          opacity={isUnavailable ? 0.5 : 1}
        />
      </Col>
      <Col flex="auto">
        <OverflowTip
          className={classnames("name", isUnavailable && "unavailable")}
        >
          {highlightText(item.itemName || "", highlightString)}
        </OverflowTip>
        <OverflowTip
          className={classnames("price", isUnavailable && "unavailable")}
        >
          {isServiceBasedMenuEnabled ? item.priceRange : item.priceDisplay}
        </OverflowTip>
      </Col>
      {renderUnavailableTag()}
      {isSellingTimeStructure
        ? showSellingTimeTag && (
            <Col>
              <SellingTimeTag
                sellingTime={getSellingTimeByID(
                  item.sellingTimeID,
                  sellingTimes,
                )}
              />
            </Col>
          )
        : spanDropdown > 0 && (
            <Col span={spanDropdown}>
              <ItemAvailabilityDropdownDuxton item={item} />
            </Col>
          )}

      {isItemWithoutPhotoMode(itemFilter) && isEditAllowed ? (
        <Col
          onClick={event => {
            event.stopPropagation()
            onClickAddPhoto()
            TroyWebTracker.sendEvent({
              category: EventCategory.menuOverviewTab,
              action: EventAction.clickAddPhoto,
              labels: {
                itemID: item.itemID,
                TRAFFIC_MEDIUM: "MISSING_PHOTO",
              },
            })
          }}
        >
          <Button type="secondary" icon={<AddImageUniversal />}>
            {textMap.addPhotoButton}
          </Button>
        </Col>
      ) : (
        <Col className={`checkbox-container ${showSelection ? "show" : ""}`}>
          <Checkbox
            defaultChecked={false}
            checked={selected}
            onChange={(e: any) => {
              handleCheck(e.target.checked)
            }}
            onClick={handleClickSelected}
          />
        </Col>
      )}
    </Row>
  )
}
